import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { LocationEntity } from './location.entity';
import { TripInspectionPhotoEntity } from './trip-inspection-photo.entity';
import { TripEntity } from './trip.entity';
import { UserEntity } from './user.entity';

export type TripInspectionType = 'checkout' | 'checkin';

export type TripInspectionStatus = 'pending' | 'in-progress' | 'completed';

export type TripInspectionInspectionStatus = 'ok' | 'issues_found';

export type TripInspectionFuelLevel = 'full' | '3/4' | '1/2' | '1/4' | 'empty';

/**
 * Pre-trip ("checkout") or post-trip ("checkin") vehicle inspection record.
 * Hangs off the Trip. UI surfaces these as "Pre-Trip Inspection" /
 * "Post-Trip Inspection" (the `checkType` enum keeps the legacy strings).
 *
 * Distinct from `TripStopInspectionEntity` (per-stop merchandiser condition
 * check).
 */
@Entity('trip_inspection')
@Index('idx_trip_inspection_trip_id', ['tripId'])
@Index('idx_trip_inspection_check_type', ['checkType'])
@Index('idx_trip_inspection_status', ['status'])
@Index('idx_trip_inspection_driver', ['driverUserId'])
@Index('idx_trip_inspection_vehicle', ['vehicleId'])
export class TripInspectionEntity extends BaseEntity {
	@Column({ name: 'fk_trip__id', type: 'int', unsigned: true })
	public tripId!: number;

	@Column({ name: 'checkType', type: 'varchar', length: 20 })
	public checkType!: TripInspectionType;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 20,
		default: "'pending'",
	})
	public status!: TripInspectionStatus;

	@Column({
		name: 'fk_location__id_vehicle',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public vehicleId?: number | null;

	@Column({
		name: 'truck_name',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	public truckName?: string | null;

	@Column({
		name: 'fk_user__id_driver',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public driverUserId?: number | null;

	@Column({
		name: 'driver_name',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	public driverName?: string | null;

	@Column({
		name: 'inspection_status',
		type: 'varchar',
		length: 20,
		nullable: true,
	})
	public inspectionStatus?: TripInspectionInspectionStatus | null;

	@Column({ name: 'condition_notes', type: 'text', nullable: true })
	public conditionNotes?: string | null;

	@Column({ name: 'mileage', type: 'int', nullable: true })
	public mileage?: number | null;

	@Column({ name: 'fuel_level', type: 'varchar', length: 10, nullable: true })
	public fuelLevel?: TripInspectionFuelLevel | null;

	@Column({ name: 'inventory_confirmed', type: 'boolean', default: false })
	public inventoryConfirmed!: boolean;

	@Column({ name: 'inventory_notes', type: 'text', nullable: true })
	public inventoryNotes?: string | null;

	@Column({ name: 'stops_confirmed', type: 'boolean', default: false })
	public stopsConfirmed!: boolean;

	@Column({ name: 'inventory_reconciled', type: 'boolean', default: false })
	public inventoryReconciled!: boolean;

	@Column({ name: 'truck_docked', type: 'boolean', nullable: true })
	public truckDocked?: boolean | null;

	@Column({ name: 'docking_notes', type: 'text', nullable: true })
	public dockingNotes?: string | null;

	@Column({ name: 'total_items_remaining', type: 'int', nullable: true })
	public totalItemsRemaining?: number | null;

	@Column({ name: 'discrepancy_notes', type: 'text', nullable: true })
	public discrepancyNotes?: string | null;

	@Column({ name: 'completed_at', type: 'datetime', nullable: true })
	public completedAt?: Date | null;

	/**
	 * On-behalf-of audit (B5, added 2026-05-22). When a warehouse
	 * manager performs an inspection (pre-trip or post-trip) on a
	 * driver's behalf — e.g. driver forgot to do post-trip before
	 * leaving the yard — this points to the driver who SHOULD have
	 * done it. The user who actually clicked through the inspection
	 * is captured via BaseEntity's modifiedUserId. Renders as
	 * "(on behalf of {driver name})" in audit feed.
	 *
	 * Note: the existing `driverUserId` field on this entity is the
	 * driver this inspection covers (truck condition, inventory).
	 * `onBehalfOfUserId` is who SHOULD have signed off but didn't,
	 * which is usually the same person — they only diverge when a WM
	 * does the work for the driver.
	 */
	@Column({
		name: 'fk_user__id_on_behalf_of',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public onBehalfOfUserId?: number | null;

	@ManyToOne(() => TripEntity, (trip) => trip.inspections)
	@JoinColumn({ name: 'fk_trip__id' })
	public fkTrip!: TripEntity;

	@ManyToOne(() => LocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_location__id_vehicle' })
	public fkVehicle?: LocationEntity;

	@ManyToOne(() => UserEntity, { nullable: true })
	@JoinColumn({ name: 'fk_user__id_driver' })
	public fkDriver?: UserEntity;

	@OneToMany(() => TripInspectionPhotoEntity, (photo) => photo.fkTripInspection)
	public photos?: Array<TripInspectionPhotoEntity>;
}
